<template>
    <div class="rvp">
        <div class="rvp-header d-flex align-items-center gap-2 mb-2">
            <VehicleNameDisplay :balise="balise" :showStatuses="true" />
        </div>

        <div v-if="canDisplayHistoryReport" class="rvp-actions d-flex flex-wrap align-items-center gap-2 mb-2">
            <button type="button" class="btn btn-sm btn-outline-secondary flex-fill text-nowrap"
                @click="toggleReportForm">
                <i class="fa-regular fa-file-pdf me-1"></i>
                Rapport véhicule
            </button>
            <router-link class="btn btn-sm btn-outline-primary flex-fill text-nowrap" :to="'/drives/' + balise.id">
                Historique
            </router-link>
        </div>

        <div v-if="canDisplayHistoryReport && showReportForm" class="rvp-report-form small mb-3">
            <div class="mb-2">
                <label class="form-label mb-1">Du</label>
                <input type="datetime-local" class="form-control form-control-sm" v-model="reportFrom">
            </div>
            <div class="mb-2">
                <label class="form-label mb-1">Au</label>
                <input type="datetime-local" class="form-control form-control-sm" v-model="reportTo">
            </div>
            <div class="d-flex justify-content-end gap-2 mt-1">
                <button type="button" class="btn btn-sm btn-link text-secondary" @click="showReportForm = false">
                    Annuler
                </button>
                <button type="button" class="btn btn-sm btn-primary" @click="onConfirmVehicleReport">
                    Télécharger
                </button>
            </div>
        </div>

        <div class="text-muted small mb-2">
            Mise à jour : {{ $filters.timeAgo(balise.lastSeen) }}<br />
            {{ balise.lastAddress }}
        </div>

        <!-- Corridoring / Geofencing (affichage fixe, sans loader) -->
        <div v-if="hasZones" class="rvp-zones mb-3">
            <div v-if="corridoringName" class="rvp-badge rvp-badge--corr">
                <i class="fa-solid fa-route me-1"></i>
                <span class="rvp-badge-label">Corridoring</span>
                <span class="rvp-badge-name">{{ corridoringName }}</span>
            </div>
            <div v-if="geofencingName" class="rvp-badge rvp-badge--geo">
                <i class="fa-solid fa-draw-polygon me-1"></i>
                <span class="rvp-badge-label">Geofencing</span>
                <span class="rvp-badge-name">{{ geofencingName }}</span>
            </div>
        </div>

        <VehicleCommands v-if="balise.commercialPackageId == 8" :balise="balise" />

        <TagControl v-if="canUseTagControl" :balise="balise" />

        <div v-if="hasDigitalIOs" class="rvp-io-section mb-3">
            <div v-if="inputDigitalIOs.length" class="rvp-io-group">
                <div class="rvp-io-group-title">Entrées</div>
                <div v-for="digitalIO in inputDigitalIOs" :key="digitalIO.id" class="rvp-io-row">
                    <div class="rvp-io-meta">
                        <span class="rvp-io-label">
                            {{ digitalIO.ioIndex ?? digitalIO.index }} :
                        </span>
                        <span class="rvp-io-state">
                            {{ getDigitalIOLabel(digitalIO) }}
                        </span>
                    </div>
                </div>
            </div>

            <div v-if="outputDigitalIOs.length" class="rvp-io-group">
                <div class="rvp-io-group-title">Sorties</div>
                <div v-for="digitalIO in outputDigitalIOs" :key="digitalIO.id" class="rvp-io-row">
                    <div class="rvp-io-meta">
                        <span class="rvp-io-label">
                            {{ digitalIO.ioIndex ?? digitalIO.index }} :
                        </span>
                        <span class="rvp-io-state">
                            {{ getDigitalIOLabel(digitalIO) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="d-grid gap-2 mt-3">
            <button v-if="!followMode" class="btn btn-success" @click="$emit('follow', balise.id)">
                <i class="fa-solid fa-play me-1"></i> Suivre
            </button>
            <button v-else class="btn btn-danger" @click="$emit('unfollow')">
                <i class="fa-solid fa-stop me-1"></i> Arrêter le suivi
            </button>
        </div>

        <div v-if="balise.commercialPackageId && balise.commercialPackageId != 1 && balise.commercialPackageId < 5"
            class="mt-3">
            <div class="d-grid gap-2">
                <button v-if="balise.parkingLat === null" class="btn btn-primary btn-sm"
                    @click="$emit('setParking', balise.id)">
                    Activer périmètre de stationnement
                </button>
                <button v-else class="btn btn-secondary btn-sm" @click="$emit('removeParking', balise.id)">
                    Fin de périmètre de stationnement
                </button>
            </div>
            <p class="text-secondary small mt-2 mb-0">
                Pensez à configurer
                <router-link :to="'/balise/' + balise.id" class="ms-1">vos notifications</router-link>.
            </p>
        </div>
    </div>
</template>

<script>
import VehicleNameDisplay from '@/components/display/VehicleNameDisplay.vue'
import CorridoringService from '@/services/corridoring.service'
import GeofencingService from '@/services/geofencing.service'
import VehicleCommands from './VehicleCommands.vue'
import TagControl from './TagControl.vue'
import FormatHelper from '@/helpers/format.helper.js'

export default {
    name: 'RightVehiclePanel',
    props: {
        balise: { type: Object, required: true },
        followMode: { type: Boolean, default: false },
        showHistoryReport: { type: Boolean, default: true },
    },
    emits: ['follow', 'unfollow', 'setParking', 'removeParking', 'vehicleReport'],
    components: {
        VehicleNameDisplay,
        VehicleCommands,
        TagControl
    },
    data() {
        return {
            showReportForm: false,
            reportFrom: '',
            reportTo: '',
            corridoringName: null,
            geofencingName: null,
        };
    },
    computed: {
        canDisplayHistoryReport() {
            return (
                this.showHistoryReport
                && this.permissions.canSeeHistoryReport
            );
        },
        hasZones() {
            return !!(this.corridoringName || this.geofencingName);
        },
        hasDigitalIOs() {
            return Array.isArray(this.balise?.digitalIOs) && this.balise.digitalIOs.length > 0;
        },
        canUseTagControl() {
            return (
                this.balise?.hasTag === true &&
                [2, 4].includes(Number(this.balise?.commercialPackageId))
            );
        },
        sortedDigitalIOs() {
            if (!this.hasDigitalIOs) {
                return [];
            }

            return [...this.balise.digitalIOs].sort((a, b) => {
                const indexA = a?.ioIndex ?? a?.index ?? 0;
                const indexB = b?.ioIndex ?? b?.index ?? 0;
                return indexA - indexB;
            });
        },
        inputDigitalIOs() {
            return this.sortedDigitalIOs.filter(digitalIO => digitalIO?.ioType === 'INPUT');
        },
        outputDigitalIOs() {
            return this.sortedDigitalIOs.filter(digitalIO => digitalIO?.ioType === 'OUTPUT');
        },
        permissions() {
            const role = this.$store.state.auth.user?.role;

            return {
                canSeeHistoryReport: !(['superoperator'].includes(role)),
            };
        }
    },
    watch: {
        balise: {
            immediate: true,
            handler(newVal, oldVal) {
                if (!newVal) return;

                const newCorrId = newVal.corridoringId || null;
                const newGeoId = newVal.geofencingId || null;
                const oldCorrId = oldVal ? oldVal.corridoringId || null : null;
                const oldGeoId = oldVal ? oldVal.geofencingId || null : null;

                // On ne recharge que si les IDs ont changé
                if (newCorrId !== oldCorrId || newGeoId !== oldGeoId) {
                    this.loadZones(newCorrId, newGeoId);
                }
            },
        },
    },
    methods: {
        getDigitalIOLabel(digitalIO) {
            if (!digitalIO) {
                return '';
            }

            if (digitalIO.value) {
                return digitalIO.onName || 'ON';
            }

            return digitalIO.offName || 'OFF';
        },
        toggleReportForm() {
            if (!this.showReportForm) {
                // Pré-remplit sur la semaine passée flottante
                const to = new Date();
                const from = new Date();
                from.setDate(to.getDate() - 7);

                const toLocal = new Date(to.getTime() - to.getTimezoneOffset() * 60000);
                const fromLocal = new Date(from.getTime() - from.getTimezoneOffset() * 60000);

                this.reportTo = toLocal.toISOString().slice(0, 16);   // "YYYY-MM-DDTHH:MM"
                this.reportFrom = fromLocal.toISOString().slice(0, 16);
            }
            this.showReportForm = !this.showReportForm;
        },
        onConfirmVehicleReport() {
            if (!this.reportFrom || !this.reportTo) {
                return;
            }

            const fromIso = new Date(this.reportFrom).toISOString();
            const toIso = new Date(this.reportTo).toISOString();

            this.$emit('vehicleReport', {
                id: this.balise.id,
                from: fromIso,
                to: toIso
            });

            this.showReportForm = false;
        },
        async loadZones(corridoringId, geofencingId) {
            // Corridoring : si plus d’ID -> on efface le nom
            if (!corridoringId) {
                this.corridoringName = null;
            }
            // Geofencing : si plus d’ID -> on efface le nom
            if (!geofencingId) {
                this.geofencingName = null;
            }

            const tasks = [];

            // Si on a un corridoringId, on (re)charge le nom
            if (corridoringId) {
                tasks.push(
                    CorridoringService.getCorridoring(corridoringId)
                        .then((resp) => {
                            const info = FormatHelper?.corridoringInfoFormat
                                ? FormatHelper.corridoringInfoFormat(resp.data)
                                : resp.data;
                            this.corridoringName = info?.name || null;
                        })
                        .catch((e) => {
                            console.error('Erreur chargement corridoring', e);
                        })
                );
            }

            // Si on a un geofencingId, on (re)charge le nom
            if (geofencingId) {
                tasks.push(
                    GeofencingService.getGeofencing(geofencingId)
                        .then((resp) => {
                            const info = FormatHelper?.geofencingInfoFormat
                                ? FormatHelper.geofencingInfoFormat(resp.data)
                                : resp.data;
                            this.geofencingName = info?.name || null;
                        })
                        .catch((e) => {
                            console.error('Erreur chargement geofencing', e);
                        })
                );
            }

            if (tasks.length) {
                try {
                    await Promise.all(tasks);
                } catch (_) {
                    // erreurs déjà loguées, on ne fait rien de spécial ici
                }
            }
        }
    }
}
</script>

<style scoped>
.rvp {
    font-size: .95rem;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: .35rem;
}

.rvp-header {
    border-bottom: 1px dashed #e9ecef;
    padding-bottom: .5rem;
    justify-content: center;
    text-align: center;
}

.rvp-actions {
    border-bottom: 1px dashed #e9ecef;
    padding-bottom: .75rem;
    margin-bottom: .75rem;
    flex-wrap: wrap;
}

.rvp-report-form {
    border: 1px solid #f1f3f5;
    border-radius: .5rem;
    padding: .5rem .75rem;
    background-color: #fcfcfc;
}

.rvp-report-form .form-label {
    font-size: .75rem;
    color: #6c757d;
}

.rvp-io-section {
    display: grid;
    gap: .75rem;
}

.rvp-io-group {
    display: grid;
    gap: .25rem;
}

.rvp-io-group-title {
    font-size: .76rem;
    font-weight: 700;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: .04em;
}

.rvp-zones {
    display: flex;
    flex-wrap: wrap;
    gap: .35rem;
}

.rvp-badge {
    display: inline-flex;
    align-items: baseline;
    gap: .35rem;
    padding: .25rem .6rem;
    border-radius: 999px;
    font-size: .8rem;
    border: 1px solid #e9ecef;
    background: #f8f9fa;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.rvp-badge--corr {
    border-color: #51cf66;
    background: rgba(81, 207, 102, .06);
}

.rvp-badge--geo {
    border-color: #339af0;
    background: rgba(51, 154, 240, .06);
}

.rvp-badge-label {
    text-transform: uppercase;
    letter-spacing: .04em;
    font-weight: 600;
    font-size: .7rem;
    color: #868e96;
}

.rvp-badge-name {
    font-weight: 500;
    color: #212529;
}


.rvp-io-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: .6rem;
    padding: .25rem .1rem;
    border-bottom: 1px dashed #f1f3f5;
    flex-wrap: wrap;
}

.rvp-io-row:last-child {
    border-bottom: 0;
    margin-bottom: .4rem;
}

.rvp-io-meta {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .45rem;
    min-width: 0;
    flex: 1 1 12rem;
}

.rvp-io-label {
    font-size: .82rem;
    font-weight: 600;
    color: #495057;
    white-space: normal;
}

.rvp-io-state {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 3.2rem;
    padding: .12rem .45rem;
    border-radius: 999px;
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .03em;
    border: 1px solid transparent;
    white-space: nowrap;
}
</style>
